import { cn } from "@/lib/utils";

interface BadgeProps {
  children: React.ReactNode;
  variant?:
    | "default"
    | "secondary"
    | "success"
    | "warning"
    | "danger"
    | "info"
    | "outline"
    | "gradient";
  size?: "xs" | "sm" | "md";
  dot?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  icon?: React.ReactNode;
  className?: string;
}

export default function Badge({
  children,
  variant = "default",
  size = "md",
  dot = false,
  removable = false,
  onRemove,
  icon,
  className,
}: BadgeProps) {
  const variants = {
    default: "bg-gray-100 text-gray-700 ring-gray-500/10",
    secondary: "bg-gray-50 text-gray-600 ring-gray-500/10",
    success: "bg-emerald-50 text-emerald-700 ring-emerald-600/10",
    warning: "bg-amber-50 text-amber-700 ring-amber-600/10",
    danger: "bg-red-50 text-red-700 ring-red-600/10",
    info: "bg-blue-50 text-blue-700 ring-blue-600/10",
    outline: "bg-transparent text-gray-700 ring-1 ring-gray-300",
    gradient:
      "bg-gradient-to-r from-primary-500 to-accent-500 text-white ring-0",
  };

  const dotColors = {
    default: "bg-gray-500",
    secondary: "bg-gray-400",
    success: "bg-emerald-500",
    warning: "bg-amber-500",
    danger: "bg-red-500",
    info: "bg-blue-500",
    outline: "bg-gray-500",
    gradient: "bg-white",
  };

  const sizes = {
    xs: "px-1.5 py-0.5 text-[10px]",
    sm: "px-2 py-0.5 text-xs",
    md: "px-2.5 py-1 text-xs",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-medium rounded-full",
        "ring-1 ring-inset",
        "transition-colors duration-200",
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {dot && (
        <span className={cn("w-1.5 h-1.5 rounded-full", dotColors[variant])} />
      )}
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            "flex-shrink-0 -mr-1 ml-0.5 p-0.5 rounded-full",
            "hover:bg-black/10 transition-colors duration-200",
            "focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400",
          )}
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
}
